/**
 * Stripe Connect Provider — wraps embedded components with AccountSession auth.
 *
 * Usage:
 *   <StripeConnectProvider accountId="acct_xxx">
 *     <ConnectPayments />
 *     <ConnectPayouts />
 *   </StripeConnectProvider>
 */
import React, { useCallback, useState } from 'react';
import { ConnectComponentsProvider } from '@stripe/react-connect-js';
import { loadConnectAndInitialize } from '@stripe/connect-js';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { AlertCircle, RefreshCw } from 'lucide-react';
import api from '@/services/api';

interface StripeConnectProviderProps {
  accountId: string;
  children: React.ReactNode;
}

export const StripeConnectProvider: React.FC<StripeConnectProviderProps> = ({
  accountId,
  children,
}) => {
  const [error, setError] = useState<string | null>(null);

  const [stripeConnectInstance] = useState(() => {
    const publishableKey = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY;
    if (!publishableKey) {
      console.error('[StripeConnectProvider] VITE_STRIPE_PUBLISHABLE_KEY is not set.');
      setError('Payment configuration is currently unavailable. Please contact system support.');
      return null;
    }

    const fetchClientSecret = async () => {
      try {
        // api.post resolves to AxiosResponse<T>; .data is the HTTP body { success, data: {...} }
        const response = await api.post<{ success: boolean; data: { clientSecret: string } }>(
          '/stripe/account-sessions',
          { accountId }
        );
        const secret = response.data?.data?.clientSecret;
        if (!secret) {
          throw new Error('No client secret returned from account session');
        }
        return secret;
      } catch (err: unknown) {
        console.error('[StripeConnectProvider] Failed to create account session:', err);
        setError('Payment configuration is currently unavailable. Please contact system support.');
        throw err;
      }
    };

    try {
      return loadConnectAndInitialize({
        publishableKey,
        fetchClientSecret,
        appearance: {
          overlays: 'dialog',
          variables: {
            colorPrimary: '#0d9488', // brand-teal — matches NovaMed clinic theme
            colorBackground: '#ffffff',
            colorText: '#0f172a',
            borderRadius: '8px',
            fontFamily: 'Inter, system-ui, sans-serif',
            fontSizeBase: '14px',
          },
        },
      });
    } catch (err: unknown) {
      console.error('[StripeConnectProvider] Failed to initialize Stripe Connect:', err);
      setError('Payment configuration is currently unavailable. Please contact system support.');
      return null;
    }
  });

  const retry = useCallback(() => {
    setError(null);
    window.location.reload();
  }, []);

  if (error) {
    return (
      <Alert variant="destructive" className="my-4">
        <AlertCircle className="h-4 w-4" />
        <AlertTitle>Payment Unavailable</AlertTitle>
        <AlertDescription className="flex items-center gap-2">
          <span>{error}</span>
          <Button variant="outline" size="sm" onClick={retry}>
            <RefreshCw className="h-3.5 w-3.5 mr-1.5" />
            Retry
          </Button>
        </AlertDescription>
      </Alert>
    );
  }

  if (!stripeConnectInstance) {
    return (
      <div className="flex items-center justify-center py-8">
        <RefreshCw className="h-5 w-5 animate-spin text-slate-400 mr-2" />
        <span className="text-sm text-slate-500">Connecting to Stripe...</span>
      </div>
    );
  }

  return (
    <ConnectComponentsProvider connectInstance={stripeConnectInstance}>
      {children}
    </ConnectComponentsProvider>
  );
};

export default StripeConnectProvider;
